import { t } from "i18next";
import { Button, Dropdown } from "react-bootstrap";
import { Gear, List, PencilSquare, PrinterFill, Trash } from "react-bootstrap-icons";
import { BsCopy } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import { CSSProperties, PropsWithChildren } from "react";
import { getGoogleAuthUrl } from "../helpers/googleSignin";
import HourglassGlobals from "../helpers/globals";
import { setSMPWRegMode } from "./register/smpwRegistration";

export const DeleteButton = (props: {
  size?: "sm" | "lg";
  onClick?: () => void;
  className?: string;
  text?: string;
  disabled?: boolean;
  variant?: string;
}) => {
  let iconSize;
  switch (props.size) {
    case "sm":
      iconSize = "1.5em";
      break;

    case "lg":
      iconSize = "1.8em";
      break;

    default:
      iconSize = "1.3em";
  }

  return (
    <Button
      size={props.size}
      variant={props.variant ?? "danger"}
      onClick={props.onClick}
      className={props.className}
      title={t("general.delete")}
      aria-label={t("general.delete")}
      disabled={props.disabled}
    >
      <Trash size={iconSize} />
      {!!props.text && <span className="ms-2">{props.text}</span>}
    </Button>
  );
};

export const EditButton = (props: {
  size?: "sm" | "lg";
  iconSize?: string;
  onClick?: (e?: any) => void;
  className?: string;
  disabled?: boolean | undefined;
  variant?: "transparent" | "secondary" | "outline" | "outline-primary" | "link" | "ghost";
  style?: CSSProperties;
}) => {
  const { t } = useTranslation();

  const iconSize = (() => {
    if (props.iconSize) return props.iconSize;

    switch (props.size) {
      case "sm":
        return "1.5em";
      case "lg":
        return "1.8em";
      default:
        return "1.3em";
    }
  })();

  return (
    <Button
      className={props.className}
      style={props.style}
      size={props.size}
      variant={props.variant}
      disabled={props.disabled}
      onClick={props.onClick}
      aria-label={t("general.edit")}
    >
      <PencilSquare size={iconSize} />
    </Button>
  );
};

export const CopyButton = (props: {
  size?: "sm" | "lg";
  onClick?: (e?: any) => void;
  className?: string;
  disabled?: boolean | undefined;
  variant?: "transparent" | "secondary";
}) => {
  let iconSize;
  switch (props.size) {
    case "sm":
      iconSize = "1.5em";
      break;

    case "lg":
      iconSize = "1.8em";
      break;

    default:
      iconSize = "1.3em";
  }

  return (
    <Button
      className={props.className}
      size={props.size}
      variant={props.variant}
      disabled={props.disabled}
      onClick={props.onClick}
      aria-label={t("general.copy")}
    >
      <BsCopy size={iconSize} />
    </Button>
  );
};

export function SettingsButton(props: { className?: string; onClick: (e?: any) => void }) {
  const { t } = useTranslation();

  return (
    <Button
      id="settings_button"
      className={props.className ?? "py-2 me-1"}
      variant="secondary"
      onClick={props.onClick}
      aria-label={t("general.settings")}
    >
      <Gear size="1.3em" />
    </Button>
  );
}

type ActionsDropdownProps = {
  className?: string;
  disabled?: boolean;
  autoClose?: boolean | "inside" | "outside";
};

export function ActionsDropdown(props: PropsWithChildren<ActionsDropdownProps>) {
  const { t } = useTranslation();

  return (
    <Dropdown>
      <Dropdown.Toggle
        className={props.className ?? "py-2 me-1"}
        variant="secondary"
        disabled={props.disabled}
        aria-label={t("userinfo.button.actions")}
      >
        <List />
      </Dropdown.Toggle>
      {props.children}
    </Dropdown>
  );
}

export function PrintDropdown(props: PropsWithChildren<ActionsDropdownProps>) {
  const { t } = useTranslation();

  return (
    <Dropdown aria-label={t("general.print")} id="print_button" autoClose={props.autoClose}>
      <Dropdown.Toggle className={props.className ?? "py-2"} variant="secondary" disabled={props.disabled}>
        <PrinterFill className="me-1" size={18} />
        <span>{t("general.print")}</span>
      </Dropdown.Toggle>
      {props.children}
    </Dropdown>
  );
}

export function GoogleSignInButtonStyling() {
  const { t } = useTranslation();

  return (
    <>
      <div className="gsi-material-button-state"></div>
      <div className="gsi-material-button-content-wrapper">
        <div className="gsi-material-button-icon">
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            style={{ display: "block" }}
          >
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            ></path>
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            ></path>
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            ></path>
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            ></path>
            <path fill="none" d="M0 0h48v48H0z"></path>
          </svg>
        </div>
        <span className="gsi-material-button-contents">{t("userauth.current-auth.method.google")}</span>
      </div>
    </>
  );
}

export function GoogleSignInButton(props: { forSmpwReg?: boolean }) {
  return (
    <a
      href={getGoogleAuthUrl(
        window.HGGlobal?.state || "state",
        HourglassGlobals.oAuthRedirectURI,
        window.HGGlobal?.nonce || "nonce",
        undefined,
        undefined,
        false,
        window.HGGlobal?.codeChallenge,
        window.HGGlobal?.codeChallengeMethod,
      )}
      onClick={() => {
        if (props.forSmpwReg) setSMPWRegMode();
      }}
      className="gsi-material-button"
    >
      <GoogleSignInButtonStyling />
    </a>
  );
}
